import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { shiftUTCPeriodHrMins } from '@iotv/datamodel-core';
import { InsertBehavior, isNonNaNNumber, localTimeToUTC, utcToLocalTime } from '@iotv/iotv-v3-types';
import { Button, FormLabel, Stack, TextField } from '@mui/material';
import { useContext, useReducer } from 'react';
import { NavSections } from '../../../../../../components/navigation/NavSections';
import { AppMainViewContext } from '../../../../../../ContextProviders/AppContextProvider';
import { AppLocalStorage } from '../../../../../../data/LocalStorage/AppLocalStorage';
import { Timezone } from '../../common/Timezone';
import { EntityProgrammeContext } from '../Reducers/ProgrammeReducer';
const debug = process.env.REACT_APP_DEBUG && false;
const isIncrementStartAction = (x) => {
    const { type, value } = x ?? {};
    return type === 'IncrementStart' && (value === null || isNonNaNNumber(value.increments));
};
const BasicStateReducer = (prev, action) => {
    const update = { ...prev };
    const { gapMins, durationMins, localHr, localMin, utcMinsOffset, repeat, repeatTimes } = action;
    if (isNonNaNNumber(repeatTimes)) {
        update.repeatTimes = repeatTimes;
    }
    if (repeat !== undefined) {
        update.repeat = repeat;
    }
    if (isNonNaNNumber(gapMins)) {
        update.gapMins = gapMins;
    }
    if (utcMinsOffset) {
        update.utcMinsOffset = utcMinsOffset;
    }
    if (isNonNaNNumber(durationMins) && durationMins > 0) {
        update.durationMins = Math.floor(durationMins);
    }
    if (isNonNaNNumber(localHr)) {
        const { utcHour, utcMin } = localTimeToUTC(localHr, update.localMin, update.utcMinsOffset);
        update.utcHour = utcHour;
        update.utcMin = utcMin;
        const local = utcToLocalTime(utcHour, utcMin, update.utcMinsOffset);
        update.localHr = local.localHr;
        update.localMin = local.localMin;
    }
    if (isNonNaNNumber(localMin)) {
        const { utcHour, utcMin } = localTimeToUTC(update.localHr, localMin, update.utcMinsOffset);
        update.utcHour = utcHour;
        update.utcMin = utcMin;
        const local = utcToLocalTime(utcHour, utcMin, update.utcMinsOffset);
        update.localHr = local.localHr;
        update.localMin = local.localMin;
    }
    return update;
};
const incrementReducer = (prev, action) => {
    const { value } = action;
    const update = { ...prev };
    const { utcHour, utcMin, durationMins, utcMinsOffset, gapMins } = update;
    const utcPeriod = {
        utc_start_hour: utcHour,
        utc_start_min: utcMin,
        duration_mins: durationMins,
    };
    const singleShiftMins = durationMins + gapMins;
    const shiftMins = value === null ? singleShiftMins : singleShiftMins * value.increments;
    const newUtcPeriod = shiftUTCPeriodHrMins(utcPeriod, shiftMins);
    const { localHr, localMin } = utcToLocalTime(newUtcPeriod.utc_start_hour, newUtcPeriod.utc_start_min, utcMinsOffset);
    Object.assign(update, {
        localHr,
        localMin,
        utcHour: newUtcPeriod.utc_start_hour,
        utcMin: newUtcPeriod.utc_start_min,
    });
    return update;
};
const reducer = (prev, action) => {
    const update = { ...prev };
    if (isIncrementStartAction(action)) {
        Object.assign(update, incrementReducer(update, action));
    }
    else {
        Object.assign(update, BasicStateReducer(update, action));
    }
    AppLocalStorage.set(NavSections.THINGVIEW, 'entityProgrammeSlotAddition', update);
    return update;
};
const getDefaultState = (utcMinsOffset) => {
    const defaultState = AppLocalStorage.get(NavSections.THINGVIEW, 'entityProgrammeSlotAddition') ?? {
        gapMins: 0,
        utcMinsOffset: utcMinsOffset ?? 0,
        localHr: 0,
        localMin: 0,
        utcHour: 0,
        utcMin: 0,
        durationMins: 1,
        repeat: false,
        repeatTimes: 0,
    };
    const { utcHour, utcMin } = localTimeToUTC(defaultState.localHr, defaultState.localMin, defaultState.utcMinsOffset);
    Object.assign(defaultState, { utcHour, utcMin });
    return defaultState;
};
export const AddSlot = ({ close }) => {
    const [{ ipData }] = useContext(AppMainViewContext);
    const { utcMinsOffset } = ipData ?? {};
    const [state, dispatch] = useContext(EntityProgrammeContext);
    const defaultState = getDefaultState(utcMinsOffset);
    const [localState, localDispatch] = useReducer(reducer, defaultState);
    const { gapMins, durationMins, utcHour, utcMin, localHr, localMin, repeat, repeatTimes } = localState;
    const { scheduler } = state;
    debug &&
        console.log(`Add Slot has`, {
            durationMins,
            utcHour,
            utcMin,
            localHr,
            localMin,
        });
    const handleAddSlot = () => {
        if (scheduler) {
            const repeats = repeat ? repeatTimes : 1;
            let utcPeriod = {
                utc_start_hour: utcHour,
                utc_start_min: utcMin,
                duration_mins: durationMins,
            };
            for (let i = 0; i < repeats; i++) {
                const { utc_start_hour } = utcPeriod;
                if (utc_start_hour > 23) {
                    utcPeriod.utc_start_hour = utc_start_hour - 24;
                }
                scheduler.insertTimeslot(utcPeriod, InsertBehavior.TRUNCATE);
                utcPeriod = shiftUTCPeriodHrMins(utcPeriod, durationMins + gapMins);
            }
            const action = {
                type: 'Recalculate',
                value: null,
            };
            dispatch(action);
            const incrementAction = {
                type: 'IncrementStart',
                value: { increments: repeats },
            };
            localDispatch(incrementAction);
        }
        close();
    };
    return (_jsxs(Stack, { gap: 1, marginBlockStart: 1, width: 'min(70cqw, 30em)', children: [_jsxs(Stack, { direction: 'row', justifyContent: 'space-between', children: [_jsx(FormLabel, { children: "Slot" }), _jsx(Timezone, {})] }), _jsxs(Stack, { direction: 'row', gap: 1, children: [_jsx(TextField, { value: localHr,
                        label: 'Start hour',
                        type: 'number',
                        size: 'small',
                        onChange: (e) => {
                            localDispatch({
                                utcMinsOffset: utcMinsOffset ?? 0,
                                localHr: parseInt(e.target.value),
                            });
                        } }), _jsx(TextField, { value: localMin,
                        label: 'Start minute',
                        type: 'number',
                        size: 'small',
                        onChange: (e) => {
                            localDispatch({
                                utcMinsOffset: utcMinsOffset ?? 0,
                                localMin: parseInt(e.target.value),
                            });
                        } })] }), _jsx(TextField, { value: durationMins,
                label: 'Slot duration (minutes)',
                type: 'number',
                size: 'small',
                onChange: (e) => {
                    localDispatch({ durationMins: parseInt(e.target.value) });
                } }), _jsx(Button, { variant: 'contained',
                onClick: () => handleAddSlot(), children: "Add" })] }));
};
